/**
 * TableToolbar — config-driven icon group that sits on the right
 * side of a table header. Each table supplies its own set of
 * actions, but the visual vocabulary stays identical: the buttons
 * are wrapped in a single bordered pill so the row reads as one
 * control strip (matches v3 design).
 *
 * `actions` is an array of either a button descriptor or a divider:
 *   { id?, icon, title, onClick, active?, disabled? }
 *   { type: 'divider' }
 *
 * `children` is the escape hatch — use it to inject MRT-aware
 * controls (density / fullscreen / show-hide columns) that need
 * access to the `table` instance. Wrap them in TableToolbarButton
 * so their styling stays consistent with the configured actions.
 */
import { styled } from '@mui/material/styles';
import TableToolbarButton from './TableToolbarButton';

const Group = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  padding: 2,
  background: 'var(--qhs-surface)',
  border: '1px solid var(--qhs-border)',
  borderRadius: 8,
});

const Divider = styled('span')({
  width: 1,
  height: 20,
  background: 'var(--qhs-border)',
  margin: '0 4px',
  alignSelf: 'center',
});

const TableToolbar = ({ actions = [], children }) => {
  if (!actions.length && !children) return null;
  return (
    <Group role="toolbar">
      {actions.map((action, idx) => {
        if (!action) return null;
        if (action.type === 'divider') {
          return <Divider key={`divider-${idx}`} aria-hidden />;
        }
        return (
          <TableToolbarButton
            key={action.id || action.title || idx}
            icon={action.icon}
            title={action.title}
            active={action.active}
            disabled={action.disabled}
            onClick={action.onClick}
            aria-label={action.ariaLabel}
          />
        );
      })}
      {children}
    </Group>
  );
};

export default TableToolbar;
